import React, { useState, useEffect } from "react";
import { getCurrentUser, fetchAuthSession, signOut } from "aws-amplify/auth";
import { configureAmplify } from "./utils/config";
import { donationsApi, expensesApi } from "./utils/api";
import Sidebar, { MobileHeader } from "./components/Sidebar";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Donations from "./pages/Donations";
import Receipts from "./pages/Receipts";
import Expenses from "./pages/Expenses";
import Finance from "./pages/Finance";
import Events from "./pages/Events";
import WhatsApp from "./pages/WhatsApp";
import Sponsors from "./pages/Sponsors";
import Members from "./pages/Members";
import Reports from "./pages/Reports";

configureAmplify();

export default function App() {
  const [activePage, setActivePage] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [authState, setAuthState] = useState("loading"); // loading | loggedIn | loggedOut
  const [userRole, setUserRole] = useState(null); // null while loading, then admin | member
  const [donations, setDonations] = useState([]);
  const [expenses, setExpenses] = useState([]);

  // Called on mount (existing session) and right after signIn()
  const resolveRole = async () => {
    try {
      let session;
      try {
        session = await fetchAuthSession({ forceRefresh: true });
      } catch {
        session = await fetchAuthSession();
      }
      const groups = session.tokens?.idToken?.payload?.["cognito:groups"] || [];
      const role = groups.some((g) => g.toLowerCase() === "admins") ? "admin" : "member";
      setUserRole(role);
      setAuthState("loggedIn");
    } catch {
      setUserRole("member");
      setAuthState("loggedIn");
    }
  };

  useEffect(() => {
    getCurrentUser().then(() => resolveRole()).catch(() => setAuthState("loggedOut"));
  }, []);

  useEffect(() => {
    if (authState !== "loggedIn") return;
    donationsApi.list()
      .then((res) => setDonations(res.donations || []))
      .catch(console.error);
    expensesApi.list()
      .then((res) => setExpenses(res.items || []))
      .catch(console.error);
  }, [authState]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {}
    setAuthState("loggedOut");
  };

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🕉️</div>
          <div className="w-8 h-8 border-3 border-orange-200 border-t-orange-500 rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-3">Loading...</p>
        </div>
      </div>
    );
  }

  if (authState === "loggedOut") {
    return <Login onLogin={resolveRole} />;
  }

  const MEMBER_PAGES = ["donations", "receipts"];
  const effectivePage =
    userRole === "member" && !MEMBER_PAGES.includes(activePage)
      ? "donations"
      : activePage;

  const sharedProps = { setActivePage, donations, expenses };

  const pageComponents = {
    dashboard: <Dashboard {...sharedProps} />,
    donations: <Donations setActivePage={setActivePage} onDonationAdded={(d) => setDonations((prev) => [d, ...prev])} />,
    receipts: <Receipts setActivePage={setActivePage} />,
    expenses: <Expenses setActivePage={setActivePage} />,
    finance: <Finance {...sharedProps} />,
    events: <Events setActivePage={setActivePage} />,
    whatsapp: <WhatsApp setActivePage={setActivePage} />,
    sponsors: <Sponsors setActivePage={setActivePage} />,
    members: <Members setActivePage={setActivePage} />,
    reports: <Reports {...sharedProps} />,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        activePage={effectivePage}
        setActivePage={setActivePage}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        onSignOut={handleSignOut}
        userRole={userRole}
      />
      <MobileHeader activePage={effectivePage} setMobileOpen={setMobileOpen} />
      <main className="mobile-safe-top lg:ml-60 min-h-screen">
        {pageComponents[effectivePage] || pageComponents.dashboard}
      </main>
    </div>
  );
}
